import { useMemo } from 'react';

export interface PasswordValidation {
  isValid: boolean;
  strength: 'weak' | 'fair' | 'good' | 'strong';
  strengthPercent: number;
  requirements: {
    minLength: boolean;
    hasUppercase: boolean;
    hasLowercase: boolean;
    hasNumber: boolean;
    hasSpecialChar: boolean;
  };
  messages: string[];
}

const MIN_LENGTH = 8;
const STRONG_LENGTH = 16;
const SPECIAL_CHARS = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;

export function usePasswordValidation(password: string): PasswordValidation {
  return useMemo(() => {
    const requirements = {
      minLength: password.length >= MIN_LENGTH,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: SPECIAL_CHARS.test(password),
    };

    const messages: string[] = [];
    if (!requirements.minLength) {
      messages.push(`At least ${MIN_LENGTH} characters`);
    }
    if (!requirements.hasUppercase) {
      messages.push('One uppercase letter');
    }
    if (!requirements.hasLowercase) {
      messages.push('One lowercase letter');
    }
    if (!requirements.hasNumber) {
      messages.push('One number');
    }
    if (!requirements.hasSpecialChar) {
      messages.push('One special character (!@#$%^&*)');
    }

    const isValid = Object.values(requirements).every(Boolean);

    // Calculate strength
    let strengthScore = 0;

    // Base requirements give 60%
    const requirementsMet = Object.values(requirements).filter(Boolean).length;
    strengthScore += (requirementsMet / 5) * 60;

    // Length bonus (up to 20% more)
    if (password.length >= STRONG_LENGTH) {
      strengthScore += 20;
    } else if (password.length >= 12) {
      strengthScore += 10;
    }

    // Variety bonus (up to 20% more)
    const uniqueChars = new Set(password).size;
    if (uniqueChars >= 12) {
      strengthScore += 20;
    } else if (uniqueChars >= 8) {
      strengthScore += 10;
    }

    const strengthPercent = Math.min(100, Math.round(strengthScore));

    let strength: 'weak' | 'fair' | 'good' | 'strong';
    if (!isValid) {
      strength = 'weak';
    } else if (strengthPercent >= 85) {
      strength = 'strong';
    } else if (strengthPercent >= 70) {
      strength = 'good';
    } else if (strengthPercent >= 50) {
      strength = 'fair';
    } else {
      strength = 'weak';
    }

    return {
      isValid,
      strength,
      strengthPercent,
      requirements,
      messages,
    };
  }, [password]);
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) && email.length <= 254;
}

/**
 * Validate display name
 */
export function isValidDisplayName(name: string): boolean {
  return name.length >= 2 && name.length <= 50 && /^[a-zA-Z0-9\s_-]+$/.test(name);
}
